import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { EmphasisBlock, CalloutBox, ButtonNode, DecklistNode, DecklistAttrs, VideoNode } from './TipTapExtensions';
import { useUploadBlogImageMutation, useUploadBlogVideoMutation } from 'store/services/blog/blogApi';
import DecklistImportModal from './DecklistImportModal';
import './TipTapEditor.css';

interface Props {
  content: any;
  onChange: (json: any, html: string) => void;
}

const MAX_PASTE_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const TipTapEditor: React.FC<Props> = ({ content, onChange }) => {
  const [uploadImage] = useUploadBlogImageMutation();
  const uploadImageRef = useRef(uploadImage);
  uploadImageRef.current = uploadImage;
  const [uploadVideo] = useUploadBlogVideoMutation();
  const uploadVideoRef = useRef(uploadVideo);
  uploadVideoRef.current = uploadVideo;
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [buttonUrl, setButtonUrl] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [showDecklistModal, setShowDecklistModal] = useState(false);
  const [editingDecklist, setEditingDecklist] = useState<DecklistAttrs | null>(null);
  const [editingDecklistPos, setEditingDecklistPos] = useState<number | null>(null);

  // Insert an image at the given position (or selection) by uploading to S3.
  // Inserts a local data-URL placeholder immediately so the user sees feedback,
  // then swaps in the S3 URL on success — or removes the placeholder on failure.
  const uploadAndInsertImage = async (view: any, file: File, atPos?: number) => {
    if (file.size > MAX_PASTE_IMAGE_SIZE) {
      toast.error(`Image too large (max ${Math.round(MAX_PASTE_IMAGE_SIZE / 1024 / 1024)}MB)`);
      return;
    }

    const placeholderId = `tiptap-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    }).catch(() => '') as string;
    if (!dataUrl) {
      toast.error('Failed to read image');
      return;
    }

    const { state } = view;
    const imageType = state.schema.nodes.image;
    if (!imageType) {
      toast.error('Image extension not loaded');
      return;
    }
    const node = imageType.create({ src: dataUrl, alt: placeholderId });
    const insertPos = typeof atPos === 'number' ? atPos : state.selection.from;
    const tr = state.tr.insert(insertPos, node);
    view.dispatch(tr);

    try {
      const formData = new FormData();
      formData.append('image', file);
      const result = await uploadImageRef.current(formData).unwrap();
      const finalUrl = result?.data?.url;
      if (!finalUrl) throw new Error('No URL returned');

      // Find placeholder node by alt and swap src
      const { state: latestState } = view;
      let foundPos: number | null = null;
      latestState.doc.descendants((n: any, pos: number) => {
        if (foundPos !== null) return false;
        if (n.type.name === 'image' && n.attrs.alt === placeholderId) {
          foundPos = pos;
          return false;
        }
        return true;
      });
      if (foundPos !== null) {
        const replaceTr = view.state.tr.setNodeMarkup(foundPos, undefined, {
          ...(view.state.doc.nodeAt(foundPos)?.attrs || {}),
          src: finalUrl,
          alt: '',
        });
        view.dispatch(replaceTr);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      toast.error('Image upload failed');
      // Remove placeholder
      const { state: latestState } = view;
      let foundPos: number | null = null;
      let foundNodeSize = 0;
      latestState.doc.descendants((n: any, pos: number) => {
        if (foundPos !== null) return false;
        if (n.type.name === 'image' && n.attrs.alt === placeholderId) {
          foundPos = pos;
          foundNodeSize = n.nodeSize;
          return false;
        }
        return true;
      });
      if (foundPos !== null) {
        const removeTr = view.state.tr.delete(foundPos, foundPos + foundNodeSize);
        view.dispatch(removeTr);
      }
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      Image,
      VideoNode,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'article-link',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start writing your article content here...',
      }),
      EmphasisBlock,
      CalloutBox,
      ButtonNode,
      DecklistNode,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const html = editor.getHTML();
      onChange(json, html);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-content',
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItems = items.filter(i => i.type.startsWith('image/'));
        if (imageItems.length === 0) return false;
        const files = imageItems.map(i => i.getAsFile()).filter((f): f is File => !!f);
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach(file => uploadAndInsertImage(view, file));
        return true;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        event.preventDefault();
        const coords = { left: event.clientX, top: event.clientY };
        const dropPos = view.posAtCoords(coords)?.pos;
        files.forEach(file => uploadAndInsertImage(view, file, dropPos));
        return true;
      },
      handleClick: (view, pos, event) => {
        // Check if clicked on a decklist block
        const target = event.target as HTMLElement;
        const decklistBlock = target.closest('.decklist-block');
        if (decklistBlock) {
          // Find the node at this position
          const { state } = view;
          const { doc } = state;

          // Search for the decklist node that contains this position
          let foundAttrs: DecklistAttrs | null = null;
          let foundPos = -1;

          doc.descendants((node, nodePos) => {
            if (node.type.name === 'decklist') {
              const nodeEnd = nodePos + node.nodeSize;
              if (pos >= nodePos && pos <= nodeEnd) {
                foundAttrs = node.attrs as DecklistAttrs;
                foundPos = nodePos;
                return false; // Stop iteration
              }
            }
            return true;
          });

          if (foundAttrs) {
            // Extract attributes and open edit modal
            setEditingDecklist(foundAttrs);
            setEditingDecklistPos(foundPos);
            setShowDecklistModal(true);
            return true; // Prevent default handling
          }
        }
        return false;
      },
    },
  });

  // Update editor content when content prop changes (e.g., when loading existing article)
  useEffect(() => {
    if (editor && !editor.isDestroyed && content) {
      const currentContent = editor.getJSON();
      // Only update if content actually changed to avoid infinite loops
      if (JSON.stringify(currentContent) !== JSON.stringify(content)) {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  // Force initial content sync when editor is ready
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const json = editor.getJSON();
      const html = editor.getHTML();
      onChange(json, html);
    }
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const formData = new FormData();
          formData.append('image', file);
          const result = await uploadImage(formData).unwrap();
          editor.chain().focus().setImage({ src: result.data.url }).run();
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      }
    };
    input.click();
  };

  const handleVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4';
    input.onchange = async (e: any) => {
      const file: File | undefined = e.target.files?.[0];
      if (!file) return;

      const isMp4Ext = file.name.toLowerCase().endsWith('.mp4');
      const isMp4Mime = file.type === 'video/mp4';
      if (!isMp4Ext || !isMp4Mime || file.size > MAX_VIDEO_SIZE) {
        toast.error('Video must be MP4 format under 50 MB');
        return;
      }
      if (file.size === 0) {
        toast.error('Video file is empty');
        return;
      }

      const placeholderId = `tiptap-video-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      editor.chain().focus().setVideo({ src: '', uploadingId: placeholderId }).run();

      try {
        const formData = new FormData();
        formData.append('video', file);
        const result = await uploadVideoRef.current(formData).unwrap();
        const finalUrl = result?.data?.url;
        if (!finalUrl) throw new Error('No URL returned');

        const { view } = editor;
        let foundPos: number | null = null;
        view.state.doc.descendants((n: any, pos: number) => {
          if (foundPos !== null) return false;
          if (n.type.name === 'video' && n.attrs.uploadingId === placeholderId) {
            foundPos = pos;
            return false;
          }
          return true;
        });
        if (foundPos !== null) {
          const tr = view.state.tr.setNodeMarkup(foundPos, undefined, {
            ...(view.state.doc.nodeAt(foundPos)?.attrs || {}),
            src: finalUrl,
            uploadingId: null,
          });
          view.dispatch(tr);
        }
      } catch (err) {
        console.error('Video upload failed:', err);
        toast.error('Video upload failed');
        const { view } = editor;
        let foundPos: number | null = null;
        let foundNodeSize = 0;
        view.state.doc.descendants((n: any, pos: number) => {
          if (foundPos !== null) return false;
          if (n.type.name === 'video' && n.attrs.uploadingId === placeholderId) {
            foundPos = pos;
            foundNodeSize = n.nodeSize;
            return false;
          }
          return true;
        });
        if (foundPos !== null) {
          const tr = view.state.tr.delete(foundPos, foundPos + foundNodeSize);
          view.dispatch(tr);
        }
      }
    };
    input.click();
  };

  const handleInsertLink = () => {
    const { selection } = editor.state;
    const selectedText = editor.state.doc.textBetween(selection.from, selection.to);

    if (selectedText) {
      setLinkText(selectedText);
      setLinkUrl('');
      setShowLinkModal(true);
    } else {
      const url = prompt('Enter URL:');
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    }
  };

  const confirmLink = () => {
    if (linkUrl) {
      if (linkText) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}" class="article-link">${linkText}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
    }
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkText('');
  };

  const handleInsertButton = () => {
    setButtonUrl('');
    setButtonText('');
    setShowButtonModal(true);
  };

  const confirmButton = () => {
    if (buttonUrl && buttonText) {
      editor.chain().focus().setButton({
        href: buttonUrl,
        text: buttonText,
        target: '_blank',
      }).run();
    }
    setShowButtonModal(false);
  };

  const handleInsertDecklist = () => {
    setEditingDecklist(null);
    setEditingDecklistPos(null);
    setShowDecklistModal(true);
  };

  const handleDecklistSave = (attrs: DecklistAttrs) => {
    if (editingDecklistPos !== null) {
      // Editing existing decklist - delete old and insert new at same position
      const { state } = editor;
      const node = state.doc.nodeAt(editingDecklistPos);
      if (node && node.type.name === 'decklist') {
        editor
          .chain()
          .focus()
          .deleteRange({ from: editingDecklistPos, to: editingDecklistPos + node.nodeSize })
          .insertContentAt(editingDecklistPos, {
            type: 'decklist',
            attrs: {
              title: attrs.title || 'Untitled Deck',
              author: attrs.author || '',
              format: attrs.format || 'Unknown Format',
              mainboard: attrs.mainboard || [],
              sideboard: attrs.sideboard || [],
            },
          })
          .run();
      }
    } else {
      // Inserting new decklist
      editor.chain().focus().setDecklist(attrs).run();
    }
    setShowDecklistModal(false);
    setEditingDecklist(null);
    setEditingDecklistPos(null);
  };

  const handleDecklistClose = () => {
    setShowDecklistModal(false);
    setEditingDecklist(null);
    setEditingDecklistPos(null);
  };

  return (
    <div className="tiptap-editor-wrapper">
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        {/* Headings */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        {/* Text formatting */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            title="Underline"
          >
            <u>U</u>
          </button>
        </div>

        {/* Lists */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            •List
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            title="Numbered List"
          >
            1.List
          </button>
          <button
            onClick={handleInsertDecklist}
            className={editor.isActive('decklist') ? 'is-active' : ''}
            title="Insert Decklist"
          >
            Decklist
          </button>
        </div>

        {/* Link & Image */}
        <div className="toolbar-group">
          <button onClick={handleVideoUpload} title="Upload Video">
            Video
          </button>
          <button onClick={handleInsertLink} title="Insert Link">
            Link
          </button>
          <button onClick={handleImageUpload} title="Upload Image">
            Image
          </button>
        </div>

        {/* Custom blocks */}
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleEmphasisBlock().run()}
            className={editor.isActive('emphasisBlock') ? 'is-active' : ''}
            title="Emphasis Block"
          >
            Emphasis
          </button>
          <button
            onClick={() => editor.chain().focus().toggleCalloutBox().run()}
            className={editor.isActive('calloutBox') ? 'is-active' : ''}
            title="Callout Box"
          >
            Callout
          </button>
          <button onClick={handleInsertButton} title="Insert Button">
            Button
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Character Count */}
      <div className="editor-footer">
        <span className="character-count">
          {editor.storage.characterCount?.characters() || editor.state.doc.textContent.length} characters
        </span>
      </div>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="modal-backdrop" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Link</h3>
            <input
              type="text"
              placeholder="Link text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              autoFocus
            />
            <input
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button onClick={confirmLink} className="primary">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Button Modal */}
      {showButtonModal && (
        <div className="modal-backdrop" onClick={() => setShowButtonModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Insert Button</h3>
            <input
              type="text"
              placeholder="Button text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              autoFocus
            />
            <input
              type="url"
              placeholder="https://example.com"
              value={buttonUrl}
              onChange={(e) => setButtonUrl(e.target.value)}
            />
            <div className="modal-actions">
              <button onClick={() => setShowButtonModal(false)}>Cancel</button>
              <button onClick={confirmButton} className="primary">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Decklist Modal */}
      <DecklistImportModal
        isOpen={showDecklistModal}
        onClose={handleDecklistClose}
        onSave={handleDecklistSave}
        initialData={editingDecklist}
      />
    </div>
  );
};

export default TipTapEditor;
